// Enums
import { PostMessageOutgoingAction } from '@/enums/PostMessageAction';

// Libraries
import debounce from 'lodash/debounce';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

// PostMessages
import { AbstractStrategy } from './abstract-strategy';

// Redux
import store from '@/redux/store';
import { setMyContacts, setMyPortfolios } from '@/redux/modules/contacts';
import { setMyHouseholds } from '@/redux/modules/household';

// Types
import AppMode from '@/types/app-mode';
import GenericContact from '@/types/generic-contact';
import Portfolio from '@/types/portfolio';
import { FactsetHouseholds } from '@/types/factset-household';
import { PostMessageRetry } from '@/redux/modules/types/post-message';
import { ReduxState } from '@/redux/modules/types';
import { Household } from '@/types/households';

// Utils
import { PostMessageSender } from '@/utils/post-messages/outgoing-messages/post-message-sender';
import { formatHousehold, formatHouseholdData } from '../../post-message-utils';
import { validateAppMode } from '@/utils/enterprise';

export class GetBookOfBusinessStrategy extends AbstractStrategy {
  execute(dispatch: ThunkDispatch<ReduxState, null, AnyAction>, context: FactsetHouseholds): void {
    // Trigger a retry if we receive an empty array of Contacts AND if the retry limit hasn't been reached
    const retryStatus: PostMessageRetry = store.getState().postMessage.retry.getContactsAndPortfolios;
    const shouldRetry: boolean = this.evaluateRetry(PostMessageOutgoingAction.FACTSET_GET_BOOK_OF_BUSINESS, retryStatus);
    const triggerRetry: () => void = debounce(
      () => PostMessageSender.getContactsAndPortfolios(store.dispatch, store.getState, retryStatus.user),
      retryStatus.delay,
    );

    if (!context.households.length && shouldRetry) {
      triggerRetry();
      return;
    }

    const formattedHouseholds: Household[] = [];
    let formattedContacts: GenericContact[] = [];
    const formattedPortfolios: Portfolio[] = [];

    context.households.forEach(household => {
      const capIntelHousehold: Household = formatHousehold(household);
      formattedHouseholds.push(capIntelHousehold);
      const { formattedContacts: contacts, formattedPortfolios: portfolios } = formatHouseholdData(household);
      formattedContacts.push(...contacts);
      formattedPortfolios.push(...portfolios);
    });

    const {
      households: { currentHousehold },
      auth: { user },
    } = store.getState();

    if (currentHousehold && user && validateAppMode(user.appConfig.mode, AppMode.CONTACT)) {
      const currentHouseholdId = currentHousehold.id;
      formattedContacts = formattedContacts.filter(contact => contact.householdId === currentHouseholdId);
    }

    dispatch(setMyContacts(formattedContacts));
    dispatch(setMyPortfolios(formattedPortfolios));
    dispatch(setMyHouseholds(formattedHouseholds, formattedPortfolios));
  }
}
